
import { useState, useEffect } from 'react';
import { VideoCarousel } from '../../../../components/VideoPlayer/VideoCarousel';
import VideoPlayerPage from '../../../../components/VideoPlayer/VideoPlayerPage';
import coachProfileService from '../../../../services/coachProfileService';

export const VideoSectionView = ({ videos = [], profile = null, playerId = null }) => {
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestNote, setRequestNote] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestSuccess, setRequestSuccess] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [hasPendingRequest, setHasPendingRequest] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  // Check for existing pending requests on mount
  useEffect(() => {
    const checkExistingRequests = async () => {
      if (!playerId) return;

      setCheckingStatus(true);
      try {
        const response = await coachProfileService.getVideoRequestsByPlayer(playerId);
        const requests = response.requests || response.data || response || [];
        // Check if there's any pending request
        const pendingExists = requests.some(req => req.status === 'pending');
        setHasPendingRequest(pendingExists);
      } catch (error) {
        console.error('Error checking video requests:', error);
      } finally {
        setCheckingStatus(false);
      }
    };

    checkExistingRequests();
  }, [playerId]);

  const handleRequestVideo = async () => {
    if (!playerId) {
      setRequestError('Player ID not found');
      return;
    }

    setRequestLoading(true);
    setRequestError(null);
    try {
      await coachProfileService.requestVideo(playerId, requestNote || 'Please upload more video footage');
      setRequestSuccess(true);
      setRequestNote('');
      setHasPendingRequest(true); // Set pending after successful request
      setTimeout(() => {
        setShowRequestModal(false);
        setRequestSuccess(false);
      }, 2000);
    } catch (error) {
      setRequestError(error.response?.data?.message || 'Failed to send video request');
    } finally {
      setRequestLoading(false);
    }
  };

  const RequestButton = () => (
    <button
      onClick={() => setShowRequestModal(true)}
      disabled={hasPendingRequest || checkingStatus}
      className={`px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-2 text-white text-[10px] sm:text-xs md:text-sm font-medium rounded-md sm:rounded-lg transition-colors flex items-center gap-1 sm:gap-1.5 md:gap-2 ${
        hasPendingRequest || checkingStatus
          ? 'bg-jp-section cursor-not-allowed opacity-60'
          : 'bg-cyan-600 hover:bg-cyan-700'
      }`}
      title={hasPendingRequest ? 'Request already pending' : ''}
    >
      {checkingStatus ? (
        <div className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg className="w-3 h-3 sm:w-3.5 sm:h-3.5 md:w-4 md:h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      )}
      <span className="hidden min-[400px]:inline">
        {hasPendingRequest ? 'Request Pending' : 'Request More Video'}
      </span>
      <span className="min-[400px]:hidden">
        {hasPendingRequest ? 'Pending' : 'Request'}
      </span>
    </button>
  );

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showRequestModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showRequestModal]);

  const closeModal = () => {
    setShowRequestModal(false);
    setRequestError(null);
    setRequestSuccess(false);
  };

  // Request Modal JSX (inlined to prevent re-mounting on state changes)
  const requestModalJSX = showRequestModal && (
    <div
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
      onClick={closeModal}
    >
      <div
        className="bg-jp-section rounded-xl border border-jp-line p-6 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-jp-ink font-bold text-lg">Request More Video</h3>
          <button
            onClick={closeModal}
            className="text-jp-muted hover:text-jp-ink transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {requestSuccess ? (
          <div className="text-center py-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-green-500">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-green-600 dark:text-green-400 font-medium">Request sent successfully!</p>
          </div>
        ) : (
          <>
            <p className="text-jp-muted text-sm mb-4">
              Send a request to the player for more video footage.
            </p>

            <textarea
              autoFocus
              dir="ltr"
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
              placeholder="Add a note (optional)... e.g., Please upload latest match footage"
              className="w-full px-4 py-3 bg-jp-card border border-jp-line rounded-lg text-jp-ink text-sm placeholder-jp-muted/70 focus:outline-none focus:border-cyan-500 resize-none text-left"
              rows={3}
            />

            {requestError && (
              <p className="text-red-600 dark:text-red-400 text-sm mt-2">{requestError}</p>
            )}

            <div className="flex gap-3 mt-4">
              <button
                onClick={closeModal}
                className="flex-1 px-4 py-2 bg-jp-section hover:bg-jp-line text-jp-ink text-sm font-medium rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestVideo}
                disabled={requestLoading}
                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-800 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {requestLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </>
                ) : (
                  'Send Request'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Show message if no videos available
  if (!videos || videos.length === 0) {
    return (
      <div className="rounded-2xl border-jp-line">
        <div className="flex items-center justify-between mb-4 sm:mb-6">
          <h2 className="text-lg font-extrabold text-jp-ink">Video</h2>
          {playerId && <RequestButton />}
        </div>
        <div className="p-6 rounded-xl bg-jp-section border border-jp-line text-center">
          <p className="text-jp-muted text-sm">No videos available</p>
        </div>
        {requestModalJSX}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-jp-line">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h2 className="text-lg font-extrabold text-jp-ink">Video</h2>
        {playerId && <RequestButton />}
      </div>

      <VideoCarousel
        videos={videos}
        onVideoClick={(video) => setSelectedVideo(video)}
        isEditing={false}
        showUploadSlots={false}
      />

      {/* Video Player Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 bg-black z-50 overflow-y-auto">
          <VideoPlayerPage
            video={selectedVideo}
            profile={profile}
            allVideos={videos}
            onClose={() => setSelectedVideo(null)}
          />
        </div>
      )}

      {/* Request Video Modal */}
      {requestModalJSX}
    </div>
  );
};

export default VideoSectionView;
